/**
 * Daily record fetch hook
 *
 * Provides the daily record data for a specific date using TanStack Query
 * with proper caching, loading states, and error handling.
 */

import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { addDays, format, parseISO, startOfDay, subDays } from "date-fns";
import {
  createApiClient,
  ApiClientError,
} from "@floline/api-client";
import type { DailyRecordResponse, UpdatePainLocationRequest } from "@floline/api-client";
import { API_URL } from "@/env";
import { dashboardKeys } from "./use-dashboard";
import { timelineRecordKeys } from "./use-timeline-records";

/** Query key factory for daily records */
export const dailyRecordKeys = {
  all: ["daily-records"] as const,
  byDate: (date: string) => [...dailyRecordKeys.all, date] as const,
};

/** API client configured for daily record requests */
const apiClient = createApiClient(
  API_URL
);

/**
 * Fetch a daily record by date from the API
 *
 * @param date - Date string in YYYY-MM-DD format
 * @returns Daily record data or null if not found
 */
async function fetchDailyRecord(date: string): Promise<DailyRecordResponse | null> {
  try {
    const response = await apiClient.getDailyRecord(date);
    return response.data;
  } catch (error: unknown) {
    // 404 means no record exists for this date - this is expected
    if (error instanceof ApiClientError && error.status === 404) {
      return null;
    }
    // Re-throw other errors to be handled by React Query
    throw error;
  }
}

/**
 * Hook to fetch a daily record by date
 *
 * Returns the daily record data along with loading and error states.
 * Returns null if no record exists for the given date (404).
 *
 * @param date - Date string in YYYY-MM-DD format
 *
 * @example
 * ```tsx
 * function DailyRecordPage() {
 *   const { date } = useParams();
 *   const { record, isLoading, isError } = useDailyRecord(date);
 *
 *   if (isLoading) return <Spinner />;
 *   if (!record) return <NoRecordFound />;
 *   return <DailyRecordView record={record} />;
 * }
 * ```
 */
export function useDailyRecord(date: string, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: dailyRecordKeys.byDate(date),
    queryFn: () => fetchDailyRecord(date),
    enabled: options?.enabled ?? true,
    // Records are relatively stable, can cache for a bit
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Don't retry on 404s (expected when no record exists)
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && error.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    // Don't refetch on mount if data is fresh
    refetchOnMount: false,
    // Keep previous day data visible while next day loads to avoid full-page flicker
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!(options?.enabled ?? true)) return;

    const currentDate = parseISO(date);
    const previousDate = format(subDays(currentDate, 1), "yyyy-MM-dd");

    queryClient.prefetchQuery({
      queryKey: dailyRecordKeys.byDate(previousDate),
      queryFn: () => fetchDailyRecord(previousDate),
      staleTime: 2 * 60 * 1000,
      retry: false,
    });

    const nextDateObj = addDays(currentDate, 1);
    const today = startOfDay(new Date());
    if (nextDateObj <= today) {
      const nextDate = format(nextDateObj, "yyyy-MM-dd");
      queryClient.prefetchQuery({
        queryKey: dailyRecordKeys.byDate(nextDate),
        queryFn: () => fetchDailyRecord(nextDate),
        staleTime: 2 * 60 * 1000,
        retry: false,
      });
    }
  }, [date, options?.enabled, queryClient]);

  return {
    /** Daily record data, or null if not found */
    record: query.data ?? null,
    /** Whether the record is currently being fetched */
    isLoading: query.isLoading,
    /** Whether the fetch failed with an error (excluding 404) */
    isError: query.isError,
    /** Whether a background fetch is currently in flight */
    isFetching: query.isFetching,
    /** Error details if the fetch failed */
    error: query.error,
    /** Whether a record exists for this date */
    exists: query.data !== null && query.data !== undefined,
    /** Refetch the record manually */
    refetch: query.refetch,
  };
}

/**
 * Hook to update a pain location entry
 *
 * Provides a mutation function for updating pain locations with optimistic updates.
 *
 * @param date - Date of the daily record being edited (for cache invalidation)
 *
 * @example
 * ```tsx
 * const { updatePainLocation, isUpdating } = useUpdatePainLocation("2026-01-11");
 *
 * await updatePainLocation({
 *   id: "uuid",
 *   data: { intensityScore: 7 }
 * });
 * ```
 */
export function useUpdatePainLocation(date: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePainLocationRequest }) => {
      const response = await apiClient.updatePainLocation(id, data);
      return response.data;
    },
    // Optimistic update
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: dailyRecordKeys.byDate(date) });

      // Snapshot the previous value
      const previousRecord = queryClient.getQueryData<DailyRecordResponse | null>(
        dailyRecordKeys.byDate(date)
      );

      // Optimistically update the cache
      if (previousRecord) {
        queryClient.setQueryData<DailyRecordResponse>(
          dailyRecordKeys.byDate(date),
          {
            ...previousRecord,
            painLocations: previousRecord.painLocations.map((loc) =>
              loc.id === id
                ? {
                    ...loc,
                    ...(data.laterality && { laterality: data.laterality }),
                    ...(data.intensityScore !== undefined && { intensityScore: data.intensityScore }),
                    ...(data.detail !== undefined && { detail: data.detail }),
                    ...(data.qualities !== undefined && { qualities: data.qualities }),
                  }
                : loc
            ),
          }
        );
      }

      return { previousRecord };
    },
    // On error, rollback to the previous value
    onError: (_err, _variables, context) => {
      if (context?.previousRecord) {
        queryClient.setQueryData(
          dailyRecordKeys.byDate(date),
          context.previousRecord
        );
      }
    },
    // Always refetch after error or success
    onSettled: () => {
      // Invalidate the specific date's daily record
      queryClient.invalidateQueries({ queryKey: dailyRecordKeys.byDate(date) });
      // Invalidate dashboard to update weekly stats (daysLogged, averageIntensity)
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      // Invalidate timeline records for consistency
      queryClient.invalidateQueries({ queryKey: timelineRecordKeys.all });
    },
  });

  return {
    /** Update a pain location */
    updatePainLocation: mutation.mutateAsync,
    /** Whether an update is in progress */
    isUpdating: mutation.isPending,
    /** Error from the last update attempt */
    updateError: mutation.error,
  };
}

/**
 * Hook to delete a pain location entry
 *
 * Provides a mutation function for deleting pain locations with optimistic updates.
 *
 * @param date - Date of the daily record being edited (for cache invalidation)
 *
 * @example
 * ```tsx
 * const { deletePainLocation, isDeleting } = useDeletePainLocation("2026-01-11");
 *
 * await deletePainLocation("uuid");
 * ```
 */
export function useDeletePainLocation(date: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deletePainLocation(id);
    },
    // Optimistic update
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: dailyRecordKeys.byDate(date) });

      // Snapshot the previous value
      const previousRecord = queryClient.getQueryData<DailyRecordResponse | null>(
        dailyRecordKeys.byDate(date)
      );

      // Optimistically update the cache
      if (previousRecord) {
        queryClient.setQueryData<DailyRecordResponse>(
          dailyRecordKeys.byDate(date),
          {
            ...previousRecord,
            painLocations: previousRecord.painLocations.filter((loc) => loc.id !== id),
          }
        );
      }

      return { previousRecord };
    },
    // On error, rollback to the previous value
    onError: (_err, _id, context) => {
      if (context?.previousRecord) {
        queryClient.setQueryData(
          dailyRecordKeys.byDate(date),
          context.previousRecord
        );
      }
    },
    // Always refetch after error or success
    onSettled: () => {
      // Invalidate the specific date's daily record
      queryClient.invalidateQueries({ queryKey: dailyRecordKeys.byDate(date) });
      // Invalidate dashboard to update weekly stats (daysLogged, averageIntensity)
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      // Invalidate timeline records for consistency
      queryClient.invalidateQueries({ queryKey: timelineRecordKeys.all });
    },
  });

  return {
    /** Delete a pain location */
    deletePainLocation: mutation.mutateAsync,
    /** Whether a delete is in progress */
    isDeleting: mutation.isPending,
    /** Error from the last delete attempt */
    deleteError: mutation.error,
  };
}
